import React from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import useWebSocket from './utils/useWebSocket.js';
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import BiodataForm from "./components/BiodataForm";
import Notification from "./components/Notification";
import OrderConfirm from "./components/OrderConfirm";
import Footer from './components/Footer';

// Modern, professional theme with Indian cultural elements
const theme = createTheme({
  palette: {
    primary: {
      main: '#008080', // Teal that complements #ffb18a
      light: '#4DB6AC',
      dark: '#00695C',
      contrastText: '#fff',
    },
    secondary: {
      main: '#D81B60',
      light: '#FF5C8D',
      dark: '#A00037',
      contrastText: '#fff',
    },
    background: {
      default: '#FAFAFA', // Clean light background
      paper: '#fff',
    },
    text: {
      primary: '#111', // Black for main titles
      secondary: '#374151',
    },
    success: {
      main: '#22c55e',
      contrastText: '#fff',
    },
    warning: {
      main: '#F59E42',
      contrastText: '#fff',
    },
    error: {
      main: '#E74C3C',
      contrastText: '#fff',
    },
  },
  typography: {
    fontFamily: 'Inter, Noto Sans, sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '2.8rem',
      lineHeight: 1.15,
      letterSpacing: '-1px',
      color: '#111', // Black title
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.2rem',
      lineHeight: 1.2,
      color: '#111',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.6rem',
      color: '#111',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: '#111',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: '#111',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: '#111',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
      fontSize: '1rem',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
          fontSize: '1rem',
          padding: '12px 28px',
          boxShadow: '0 2px 8px rgba(30, 41, 59, 0.08)',
          transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
          textTransform: 'none',
        },
        contained: {
          background: undefined, // Use default MUI color
        },
        outlined: {
          borderWidth: '2px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.04)',
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#FF8C42',
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#FF8C42',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.04)',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '16px 0',
          },
          '& .MuiAccordionSummary-root': {
            borderBottom: 'none',
          },
          '& .MuiAccordionSummary-root.Mui-expanded': {
            borderBottom: 'none',
          },
          '& .MuiAccordionDetails-root': {
            paddingTop: '24px',
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          minHeight: 64,
          borderBottom: 'none',
          '&.Mui-expanded': {
            minHeight: 64,
            borderBottom: 'none',
          },
          '&:hover': {
            borderBottom: 'none',
          },
        },
        content: {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

function App() {
  const { onlineUsers, error, status } = useWebSocket();

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/" element={
            <div role="main">
              <BiodataForm />
              <Notification onlineUsers={onlineUsers} error={error} status={status} />
            </div>
          } />
          <Route path="/order-confirm" element={<OrderConfirm />} />
        </Routes>
        <Footer />
      </ThemeProvider>
    </Router>
  );
}

export default App;